import { PatientLayout } from "@/components/layout/patient-layout";
import { useGetCondition, getGetConditionQueryKey } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Info, Pill, Heart, HelpCircle, Stethoscope } from "lucide-react";
import { UkHealthDisclaimer } from "@/components/ui/uk-health-disclaimer";
import { useEffect } from "react";
import { recordRecentView } from "@/hooks/use-recently-viewed";
import { BackButton } from "@/components/ui/back-button";

type ClinicalFeature = { feature: string; detail: string };

export default function PatientConditionDetail() {
  const { id } = useParams();
  const { data: condition, isLoading, error } = useGetCondition(Number(id), {
    query: { enabled: !!id, queryKey: getGetConditionQueryKey(Number(id)) }
  });

  useEffect(() => {
    if (!condition) return;
    recordRecentView({
      type: "condition",
      id: condition.id,
      name: condition.name,
      simpleName: condition.simpleName ?? undefined,
      href: `/patient/conditions/${condition.id}`,
    });
  }, [condition?.id]);

  if (isLoading) return <PatientLayout title="Know Your Condition"><LoadingScreen /></PatientLayout>;
  if (error || !condition) return <PatientLayout title="Know Your Condition"><div className="p-8 text-slate-500">Information not found</div></PatientLayout>;

  const clinicalFeatures = (condition.clinicalFeatures ?? []) as ClinicalFeature[];

  return (
    <PatientLayout title={condition.name}>
      <BackButton href="/patient/conditions">Back to Conditions</BackButton>

      <UkHealthDisclaimer variant="patient" compact />

      {/* Hero Header */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-rose-50 to-orange-50 p-8 sm:p-12 border-b border-rose-100">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">{condition.name}</h1>
          {condition.simpleName && condition.simpleName !== condition.name && (
            <p className="text-lg text-slate-500">Sometimes called: {condition.simpleName}</p>
          )}
          {condition.prevalence && (
            <p className="mt-3 text-sm text-rose-700 bg-rose-100 rounded-full px-4 py-1.5 inline-block">
              {condition.prevalence}
            </p>
          )}
        </div>

        <div className="p-8 sm:p-12">
          <div className="flex items-start gap-4">
            <div className="bg-rose-100 p-3 rounded-2xl text-rose-600 shrink-0 mt-1">
              <Info className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">What is it?</h2>
              <p className="text-slate-700 leading-relaxed text-lg">
                {condition.patientDescription}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Common Symptoms */}
      {condition.keySymptoms && condition.keySymptoms.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-amber-100 p-2.5 rounded-2xl">
              <Heart className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Common Symptoms</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {condition.keySymptoms.map((s, i) => (
              <div key={i} className="flex items-start gap-3 bg-amber-50 rounded-xl p-4 border border-amber-100">
                <div className="h-5 w-5 rounded-full bg-amber-400 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</div>
                <span className="text-slate-800">{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* What to Expect - Clinical Features in plain language */}
      {clinicalFeatures.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-2.5 rounded-2xl">
              <Stethoscope className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">What to Expect</h2>
          </div>
          <div className="space-y-4">
            {clinicalFeatures.slice(0, 4).map((f, i) => (
              <div key={i} className="border border-slate-100 rounded-2xl p-5 hover:border-blue-200 hover:bg-blue-50/30 transition-colors">
                <h4 className="font-semibold text-slate-900 mb-2 text-lg">{f.feature}</h4>
                <p className="text-slate-600 leading-relaxed">{f.detail}</p>
              </div>
            ))}
            {clinicalFeatures.length > 4 && (
              <p className="text-sm text-slate-500 text-center pt-2">
                Your specialist team will explain all aspects of your condition in detail during your appointments.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Medications */}
      {condition.relatedMedications && condition.relatedMedications.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-indigo-100 p-2.5 rounded-2xl">
              <Pill className="h-6 w-6 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Common Treatments</h2>
          </div>
          <p className="text-slate-600 mb-5">These are some of the medicines that may be used to treat this condition. Your doctor will decide which is right for you.</p>
          <div className="flex flex-wrap gap-3 mb-5">
            {condition.relatedMedications.map(m => (
              <span key={m} className="px-4 py-2 bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-full text-sm font-medium">
                {m}
              </span>
            ))}
          </div>
          <p className="text-sm text-slate-400 border-t border-slate-100 pt-4">
            Always take medication exactly as prescribed. Ask your specialist nurse or pharmacist if you have any questions.
          </p>
        </div>
      )}

      {/* FAQ / Helpful Advice */}
      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-3xl p-8 border border-teal-100 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <HelpCircle className="h-6 w-6 text-teal-600 shrink-0" />
          <h3 className="text-xl font-bold text-teal-900">Good to Know</h3>
        </div>
        <ul className="space-y-3">
          <li className="flex items-start gap-2 text-teal-800 text-sm">
            <span className="font-bold shrink-0">✓</span>
            Most rheumatic conditions can be well managed with the right treatment.
          </li>
          <li className="flex items-start gap-2 text-teal-800 text-sm">
            <span className="font-bold shrink-0">✓</span>
            Attending regular appointments helps your team monitor your condition.
          </li>
          <li className="flex items-start gap-2 text-teal-800 text-sm">
            <span className="font-bold shrink-0">✓</span>
            Our AI guide can answer questions about your condition anytime — use the chat feature.
          </li>
        </ul>
      </div>

      {/* NICE Guideline */}
      {condition.niceGuideline && (
        <div className="bg-slate-800 rounded-3xl p-8 text-white">
          <h3 className="text-lg font-bold mb-2">NHS / NICE Information</h3>
          <p className="text-slate-300 text-sm mb-4">
            Clinical guidelines reference: {condition.niceGuideline}. Your care team follows national evidence-based guidelines.
          </p>
          <a href={`https://www.nice.org.uk/guidance/${condition.niceGuideline.toLowerCase()}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600 hover:text-white">
              View NICE Information →
            </Button>
          </a>
        </div>
      )}
    </PatientLayout>
  );
}
